/**
 * ChatChartCard
 * Renders interactive chart visualizations in chat messages.
 * Uses recharts for line, bar, and pie charts.
 */

import type { ChartCardData } from '@parlay-picker/shared/chatTypes';
import { ArrowDown, ArrowUp, TrendingUp } from 'lucide-react';
import type { ReactElement } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface ChatChartCardProps {
  data: ChartCardData;
  onSendMessage?: (message: string) => void;
}

// Recharts Tooltip formatter — recharts types are too loose to type precisely
// biome-ignore lint/suspicious/noExplicitAny: recharts Formatter type is untyped
function makeTooltipFormatter(formatValue: (v: number) => string): any {
  return (v: number, _name: string, entry: { payload?: { sampleSize?: number } }) => [
    `${formatValue(Number(v ?? 0))}${entry?.payload?.sampleSize ? ` (${entry.payload.sampleSize} samples)` : ''}`,
    'Rate',
  ];
}

// Theme-aware colors via CSS custom properties
const CHART_COLORS = [
  'var(--color-primary, #6366f1)',
  'var(--color-success, #22c55e)',
  'var(--color-warning, #f59e0b)',
  'var(--color-danger, #ef4444)',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#14b8a6',
];

export function ChatChartCard({ data, onSendMessage }: ChatChartCardProps): ReactElement {
  const chartType = data.chartType || 'trend_line';
  const title = data.title || 'Chart';
  const subtitle = data.subtitle || null;
  const followUpChips = data.followUpChips || [];

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-text">{title}</span>
        {subtitle && (
          <span className="text-[10px] text-theme-text-tertiary ml-auto">{subtitle}</span>
        )}
      </div>

      {/* Chart body */}
      <div className="min-h-[160px]">
        {chartType === 'trend_line' && <TrendLineChart data={data} onSendMessage={onSendMessage} />}
        {chartType === 'bar_comparison' && (
          <BarComparisonChart data={data} onSendMessage={onSendMessage} />
        )}
        {chartType === 'pie_breakdown' && <PieBreakdownChart data={data} />}
        {chartType === 'multi_stat' && <MultiStatDashboard data={data} />}
      </div>

      {/* Follow-up chips */}
      {followUpChips.length > 0 && onSendMessage && (
        <div className="flex gap-1.5 flex-wrap pt-1 border-t border-theme-border/50">
          {followUpChips.map((chip) => (
            <button
              type="button"
              key={chip}
              onClick={() => onSendMessage(chip)}
              className="text-[10px] px-2 py-0.5 rounded-full bg-theme-primary/10 text-theme-primary hover:bg-theme-primary/20 transition-colors"
            >
              {chip}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Trend Line Chart
// =============================================================================

function TrendLineChart({
  data,
  onSendMessage,
}: {
  data: ChartCardData;
  onSendMessage?: (message: string) => void;
}): ReactElement {
  const points = data.points || [];
  const referenceLine = data.referenceLine;
  const valueFormat = data.valueFormat || 'percentage';

  if (points.length === 0) {
    return (
      <div className="text-xs text-theme-text-secondary text-center py-8">No data available</div>
    );
  }

  const formatValue = (v: number) => (valueFormat === 'percentage' ? `${v}%` : String(v));

  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart
        data={points}
        onClick={(e) => {
          if (e?.activeLabel && onSendMessage) {
            onSendMessage(`Tell me about my performance on ${e.activeLabel}`);
          }
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #333)" opacity={0.3} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 9, fill: 'var(--color-text-tertiary, #888)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 9, fill: 'var(--color-text-tertiary, #888)' }}
          axisLine={false}
          tickLine={false}
          width={35}
          tickFormatter={(v) => formatValue(v)}
        />
        <Tooltip
          contentStyle={{
            background: 'var(--color-surface, #1a1a1a)',
            border: '1px solid var(--color-border, #333)',
            borderRadius: 8,
            fontSize: 11,
          }}
          formatter={makeTooltipFormatter(formatValue)}
        />
        {referenceLine && (
          <ReferenceLine
            y={referenceLine.value}
            stroke="var(--color-text-tertiary, #888)"
            strokeDasharray="4 4"
            label={{
              value: referenceLine.label,
              position: 'right',
              fontSize: 9,
              fill: 'var(--color-text-tertiary, #888)',
            }}
          />
        )}
        <Line
          type="monotone"
          dataKey="value"
          stroke="var(--color-primary, #6366f1)"
          strokeWidth={2}
          dot={{ r: 3, fill: 'var(--color-primary, #6366f1)', cursor: 'pointer' }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// =============================================================================
// Bar Comparison Chart
// =============================================================================

function BarComparisonChart({
  data,
  onSendMessage,
}: {
  data: ChartCardData;
  onSendMessage?: (message: string) => void;
}): ReactElement {
  const bars = data.bars || [];
  const valueFormat = data.valueFormat || 'percentage';

  if (bars.length === 0) {
    return (
      <div className="text-xs text-theme-text-secondary text-center py-8">No data available</div>
    );
  }

  const formatValue = (v: number) => (valueFormat === 'percentage' ? `${v}%` : String(v));

  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart
        data={bars}
        onClick={(e) => {
          if (e?.activeLabel && onSendMessage) {
            onSendMessage(`Tell me more about my ${e.activeLabel} performance`);
          }
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #333)" opacity={0.3} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 9, fill: 'var(--color-text-tertiary, #888)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 9, fill: 'var(--color-text-tertiary, #888)' }}
          axisLine={false}
          tickLine={false}
          width={35}
          tickFormatter={(v) => formatValue(v)}
        />
        <Tooltip
          contentStyle={{
            background: 'var(--color-surface, #1a1a1a)',
            border: '1px solid var(--color-border, #333)',
            borderRadius: 8,
            fontSize: 11,
          }}
          formatter={makeTooltipFormatter(formatValue)}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer">
          {bars.map((entry, i) => (
            <Cell
              key={i}
              fill={
                entry.highlight ? 'var(--color-success, #22c55e)' : 'var(--color-primary, #6366f1)'
              }
              opacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// =============================================================================
// Pie Breakdown Chart
// =============================================================================

function PieBreakdownChart({ data }: { data: ChartCardData }): ReactElement {
  const segments = data.segments || [];

  if (segments.length === 0) {
    return (
      <div className="text-xs text-theme-text-secondary text-center py-8">No data available</div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width={120} height={120}>
        <PieChart>
          <Pie
            data={segments}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            outerRadius={50}
            innerRadius={25}
          >
            {segments.map((_entry, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              background: 'var(--color-surface, #1a1a1a)',
              border: '1px solid var(--color-border, #333)',
              borderRadius: 8,
              fontSize: 11,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex-1 space-y-1">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
            />
            <span className="text-theme-text">{seg.label}</span>
            <span className="text-theme-text-tertiary ml-auto">{seg.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Multi-Stat Dashboard
// =============================================================================

function MultiStatDashboard({ data }: { data: ChartCardData }): ReactElement {
  const metrics = data.metrics || [];

  if (metrics.length === 0) {
    return (
      <div className="text-xs text-theme-text-secondary text-center py-8">No data available</div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {metrics.map((m, i) => {
        const delta = m.previous != null ? m.current - m.previous : null;
        const formatValue = (v: number) => (m.format === 'percentage' ? `${v}%` : String(v));

        return (
          <div key={i} className="bg-theme-bg rounded-lg p-2.5 text-center">
            <div className="text-[10px] text-theme-text-tertiary mb-0.5">{m.label}</div>
            <div className="text-lg font-bold text-theme-text">{formatValue(m.current)}</div>
            {delta != null && delta !== 0 && (
              <div
                className={`flex items-center justify-center gap-0.5 text-[10px] ${delta > 0 ? 'text-theme-success' : 'text-theme-danger'}`}
              >
                {delta > 0 ? (
                  <ArrowUp className="w-2.5 h-2.5" />
                ) : (
                  <ArrowDown className="w-2.5 h-2.5" />
                )}
                {Math.abs(delta).toFixed(1)}
                {m.format === 'percentage' ? '%' : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
